/**
 * Cart Drawer hydration (bol.com success view)
 * --------------------------------------------------------
 * Runs alongside Dawn's cart.js + cart-drawer.js. When an item is added to
 * cart, this script:
 *   1. Captures the last-added product data from the cartUpdate event
 *   2. On drawer open, hydrates the "just added" card with the correct item
 *      (handles duplicates — where cart.items.last would be stale)
 *   3. Fetches complementary products for that product and renders them in
 *      the upsell track
 *   4. Also handles inline upsell ATC (clicking + on an upsell card)
 */
(function () {
  'use strict';

  var UPSELL_ENDPOINT = '/recommendations/products';
  var UPSELL_SECTION_ID = 'es-cart-upsells';
  var UPSELL_LIMIT = 6;

  // PostHog: track begin_checkout on any "proceed to checkout" / "ga verder naar bestellen" click
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-es-begin-checkout], [name="checkout"], a[href="/checkout"], a[href*="/checkout"]');
    if (!trigger) return;
    if (window.posthog && typeof window.posthog.capture === 'function') {
      try {
        window.posthog.capture('begin_checkout', {
          source: trigger.hasAttribute('data-es-begin-checkout') ? 'cart-drawer' : 'cart-page'
        });
      } catch (err) { console.warn('[posthog] begin_checkout capture failed', err); }
    }
  });

  /** @type {object|null} */
  var lastAdded = null;

  /** @type {boolean} */
  var upsellsLoadedFor = null;

  // ── Utilities ────────────────────────────────────────────────────────────

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function formatMoney(cents) {
    var num = Number(cents) / 100;
    if (isNaN(num)) return '';
    return '\u20AC\u00A0' + num.toFixed(2).replace('.', ',');
  }

  function $(sel, root) {
    return (root || document).querySelector(sel);
  }

  // ── Last-added extraction ────────────────────────────────────────────────

  /**
   * Normalizes a cartUpdate event's cartData into our lastAdded shape.
   * Handles two /cart/add.js response shapes:
   *   1. Single-item response — top-level item fields (product_id, id, title, etc.)
   *   2. Multi-item response — { items: [...] }
   */
  function extractLastAdded(cartData) {
    if (!cartData) return null;

    // Multi-item response
    if (Array.isArray(cartData.items) && cartData.items.length) {
      var first = cartData.items[0];
      var normalized = normalizeItem(first);
      if (cartData.items.length > 1) {
        normalized.extraCount = cartData.items.length - 1;
      }
      return normalized;
    }

    // Single-item response (has product_id at top level)
    if (cartData.product_id) {
      return normalizeItem(cartData);
    }

    return null;
  }

  function normalizeItem(item) {
    // Shopify uses `id` for variant_id in /cart/add.js responses
    var variantId = item.variant_id != null ? item.variant_id : item.id;
    var productTitle = item.product_title || item.title || '';
    var originalPrice = item.original_price != null ? item.original_price : item.price;
    var originalLinePrice = item.original_line_price != null ? item.original_line_price : null;
    return {
      productId: item.product_id,
      productHandle: item.handle || item.product_handle,
      variantId: variantId,
      title: productTitle,
      variantTitle: item.variant_title,
      image: item.image || (item.featured_image && item.featured_image.url) || '',
      price: item.final_price != null ? item.final_price : item.price,
      originalPrice: originalPrice,
      linePrice: item.final_line_price != null ? item.final_line_price : null,
      originalLinePrice: originalLinePrice,
      quantity: item.quantity || 1,
      url: item.url || (item.handle ? '/products/' + item.handle : '#'),
      properties: item.properties || null,
      extraCount: 0,
    };
  }

  // Build markup for visible line-item properties (e.g. greeting card
  // message). Skips empty values and Shopify's private `_`-prefixed keys.
  function propertiesHtml(properties) {
    if (!properties) return '';
    var html = '';
    var keys = Object.keys(properties);
    for (var i = 0; i < keys.length; i++) {
      var key = keys[i];
      var value = properties[key];
      if (!value || key.charAt(0) === '_') continue;
      html +=
        '<p class="es-cart-drawer__item-property">' +
          '<span>' + escapeHtml(key) + ':</span> ' + escapeHtml(value) +
        '</p>';
    }
    return html;
  }

  // ── Hydration ────────────────────────────────────────────────────────────

  function hydrateJustAdded() {
    var slot = $('[data-es-cart-just-added]');
    if (!slot || !lastAdded) return;

    var linePrice = lastAdded.linePrice != null
      ? lastAdded.linePrice
      : (lastAdded.price || 0) * (lastAdded.quantity || 1);

    var extraNote = lastAdded.extraCount > 0
      ? '<p class="es-cart-drawer__item-extra">+ ' + lastAdded.extraCount + ' meer items</p>'
      : '';

    var imgHtml = lastAdded.image
      ? '<div class="es-cart-drawer__item-img-wrap">' +
        '<img src="' + escapeHtml(lastAdded.image) + '" alt="' + escapeHtml(lastAdded.title) +
        '" width="88" height="88" loading="lazy" class="es-cart-drawer__item-img">' +
        '</div>'
      : '';

    var variantHtml = lastAdded.variantTitle && lastAdded.variantTitle !== 'Default Title'
      ? '<p class="es-cart-drawer__item-variant">' + escapeHtml(lastAdded.variantTitle) + '</p>'
      : '';

    slot.innerHTML =
      '<a href="' + escapeHtml(lastAdded.url) + '" class="es-cart-drawer__item">' +
        imgHtml +
        '<div class="es-cart-drawer__item-info">' +
          '<h3 class="es-cart-drawer__item-title">' + escapeHtml(lastAdded.title) + '</h3>' +
          variantHtml +
          propertiesHtml(lastAdded.properties) +
          '<p class="es-cart-drawer__item-meta">' +
            '<span class="es-cart-drawer__item-qty">' + lastAdded.quantity + '×</span> ' +
            (function() {
              var origLine = lastAdded.originalLinePrice != null ? lastAdded.originalLinePrice : (lastAdded.originalPrice || 0) * (lastAdded.quantity || 1);
              if (origLine > linePrice) {
                return '<span class="es-cart-drawer__item-price--compare">' + formatMoney(origLine) + '</span> ' +
                       '<span class="es-cart-drawer__item-price es-cart-drawer__item-price--sale">' + formatMoney(linePrice) + '</span>';
              }
              return '<span class="es-cart-drawer__item-price">' + formatMoney(linePrice) + '</span>';
            })() +
          '</p>' +
          extraNote +
        '</div>' +
      '</a>';
  }

  // ── Upsells fetch ────────────────────────────────────────────────────────

  function fetchUpsells(productId) {
    if (!productId || upsellsLoadedFor === productId) return;
    upsellsLoadedFor = productId;

    var url = UPSELL_ENDPOINT +
      '?intent=complementary' +
      '&product_id=' + encodeURIComponent(productId) +
      '&limit=' + UPSELL_LIMIT +
      '&section_id=' + UPSELL_SECTION_ID;

    fetch(url)
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var wrap = $('[data-es-cart-upsells]');
        var track = $('[data-es-cart-upsells-track]');
        if (!wrap || !track) return;

        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var list = parsed.querySelector('[data-es-cart-upsells-list]');
        if (!list || !list.children.length) {
          wrap.hidden = true;
          return;
        }

        track.innerHTML = '';
        Array.prototype.forEach.call(list.children, function (card) {
          // Edge case: if the user just added the FORCED upsell product
          // itself (e.g. potgrond → potgrond), the section template still
          // appends it as a forced upsell. Strip those self-recommendations
          // here so we don't show "Vergeet niet?" with the exact same item
          // they just added.
          var cardHandle = card.getAttribute('data-product-handle');
          if (cardHandle && lastAdded && lastAdded.productHandle && cardHandle === lastAdded.productHandle) {
            return;
          }
          track.appendChild(card.cloneNode(true));
        });
        // If filtering left the track empty, hide the wrapper.
        if (track.children.length === 0) {
          wrap.hidden = true;
        } else {
          wrap.hidden = false;
        }
      })
      .catch(function (err) {
        console.warn('[es-cart-drawer] upsells fetch failed', err);
      });
  }

  // ── Inline upsell ATC ────────────────────────────────────────────────────

  function handleUpsellSubmit(e) {
    var form = e.target.closest('[data-es-upsell-form]');
    if (!form) return;
    e.preventDefault();

    var btn = form.querySelector('button[type="submit"]');
    if (btn) btn.classList.add('is-loading');

    var variantId = form.querySelector('[name="id"]').value;
    var cartDrawer = document.querySelector('cart-drawer');
    var sectionsToFetch = [];
    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      sectionsToFetch = cartDrawer.getSectionsToRender().map(function (s) { return s.section || s.id; });
    }

    var body = {
      items: [{ id: Number(variantId), quantity: 1 }],
    };
    if (sectionsToFetch.length) {
      body.sections = sectionsToFetch;
      body.sections_url = window.location.pathname;
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify(body),
    })
      .then(function (res) { return res.json(); })
      .then(function (parsedState) {
        if (parsedState.status) {
          console.error('[es-cart-drawer] upsell ATC error', parsedState);
          return;
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'es-cart-drawer-upsell',
            productVariantId: variantId,
            cartData: parsedState,
          });
        }

        if (cartDrawer && parsedState.sections && typeof cartDrawer.renderContents === 'function') {
          // Reset upsells cache so new product fetches fresh recs
          upsellsLoadedFor = null;
          cartDrawer.renderContents(parsedState);
        }
      })
      .catch(function (err) {
        console.error('[es-cart-drawer] upsell fetch failed', err);
      })
      .finally(function () {
        if (btn) btn.classList.remove('is-loading');
      });
  }

  // ── Drawer open observer ─────────────────────────────────────────────────

  function onDrawerOpen() {
    hydrateJustAdded();
    if (lastAdded && lastAdded.productId) {
      fetchUpsells(lastAdded.productId);
    }
  }

  function observeDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    var wasActive = drawer.classList.contains('active');
    var obs = new MutationObserver(function () {
      var isActive = drawer.classList.contains('active');
      if (isActive && !wasActive) {
        onDrawerOpen();
      }
      wasActive = isActive;
    });
    obs.observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  // ── Header cart badge updater ────────────────────────────────────────────

  /**
   * Update the desktop header cart count badges. The es-header.liquid
   * template renders #es-cart-badge and #es-cart-count-inline as static
   * server-rendered values, and Dawn's cart.js doesn't know about them
   * (it only updates #cart-icon-bubble which is the legacy Dawn header).
   * Without this, after removing items on /cart, the visible badge stays
   * at the old count even though the cart is empty server-side.
   */
  function updateHeaderCartBadges(cartData) {
    var count = null;
    if (cartData && typeof cartData.item_count === 'number') {
      count = cartData.item_count;
    }
    if (count === null) return;
    var nStr = String(count);
    var display = count > 99 ? '99+' : nStr;
    var selectors = [
      '#es-cart-badge',
      '#es-cart-count-inline',
      '[data-cart-count]',
      '#es-cart-count',
    ];
    selectors.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) {
        el.textContent = display;
        el.setAttribute('data-count', nStr);
        // Hide the count badges that opt out of zero
        if (count === 0 && el.matches('[data-count="0"], #es-cart-badge, #es-cart-count-inline')) {
          // CSS already hides via [data-count="0"] selector — no JS toggle needed
        }
      });
    });
  }

  // ── Event subscription ───────────────────────────────────────────────────

  function subscribeToCartUpdates() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
      // global.js hasn't loaded yet — retry
      setTimeout(subscribeToCartUpdates, 50);
      return;
    }
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (!event) return;
      // Always update header badges, even on cart-items source events,
      // because cart.js doesn't update the theme-specific badges.
      updateHeaderCartBadges(event.cartData);
      // For drawer hydration: only react to product-form / es-pdp / etc.,
      // NOT to cart-items source (which means user is editing cart, not adding).
      if (event.source === 'cart-items') return;
      var extracted = extractLastAdded(event.cartData);
      if (extracted) {
        lastAdded = extracted;
        // Invalidate upsells cache when a different product is added
        if (upsellsLoadedFor && upsellsLoadedFor !== extracted.productId) {
          upsellsLoadedFor = null;
        }
      }
    });
  }

  // ── Init ─────────────────────────────────────────────────────────────────

  function init() {
    subscribeToCartUpdates();
    observeDrawer();
    document.addEventListener('submit', handleUpsellSubmit);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
